import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { clearCredentials, refreshExpiry, selectExpiresAt, selectToken } from "../store/authSlice";
import { useToast } from "../context/Toast/useToast";

const WARN_BEFORE_MS = 5 * 60 * 1000;
const CHECK_INTERVAL_MS = 30 * 1000;

export default function useAutoLogout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const expiresAt = useSelector(selectExpiresAt);
  const token = useSelector(selectToken);
  const warnedRef = useRef(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!token || !expiresAt) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    warnedRef.current = false;

    const check = () => {
      const now = Date.now();
      const remaining = expiresAt - now;

      if (remaining <= 0) {
        clearInterval(intervalRef.current);
        dispatch(clearCredentials());
        toast.error("Session expired. Please log in again.");
        navigate("/login?session=expired", { replace: true });
        return;
      }

      if (remaining <= WARN_BEFORE_MS && !warnedRef.current) {
        warnedRef.current = true;
        toast.warning("Your session expires in 5 minutes.", 8000);
      }
    };

    check();
    intervalRef.current = setInterval(check, CHECK_INTERVAL_MS);

    return () => clearInterval(intervalRef.current);
  }, [token, expiresAt, dispatch, navigate]);

  useEffect(() => {
    if (!token) return;

    const extend = () => dispatch(refreshExpiry());

    const events = ["click", "keydown", "mousemove", "touchstart"];
    events.forEach((e) => window.addEventListener(e, extend, { passive: true }));

    return () => events.forEach((e) => window.removeEventListener(e, extend));
  }, [token, dispatch]);
}
